/**
 * Project data model for CoreSkan
 */

// Photo category types
export interface PhotoCategory {
  id: string;
  name: string;
  color: string;  // Hex color for marker
  icon: 'camera' | 'warning' | 'check' | 'flag' | 'pin' | 'star' | 'info' | 'tool' | 'building' | 'electrical' | 'plumbing' | 'hvac' | 'structural' | 'fire' | 'exterior' | 'interior' | 'drone';
}

// Default photo categories - comprehensive list for construction/survey
export const DEFAULT_PHOTO_CATEGORIES: PhotoCategory[] = [
  // Aerial
  { id: 'drone', name: 'Drone', color: '#4F7CFF', icon: 'drone' },
  // General
  { id: 'general', name: 'General', color: '#4F7CFF', icon: 'camera' },
  { id: 'progress', name: 'Progress', color: '#00cc88', icon: 'check' },
  { id: 'issue', name: 'Issue/Defect', color: '#ff4444', icon: 'warning' },
  { id: 'safety', name: 'Safety', color: '#ffaa00', icon: 'flag' },
  // Building Systems
  { id: 'electrical', name: 'Electrical', color: '#ffd700', icon: 'electrical' },
  { id: 'plumbing', name: 'Plumbing', color: '#1e90ff', icon: 'plumbing' },
  { id: 'hvac', name: 'HVAC', color: '#87ceeb', icon: 'hvac' },
  { id: 'structural', name: 'Structural', color: '#8b4513', icon: 'structural' },
  { id: 'fire', name: 'Fire Protection', color: '#dc143c', icon: 'fire' },
  // Areas
  { id: 'exterior', name: 'Exterior', color: '#228b22', icon: 'exterior' },
  { id: 'interior', name: 'Interior', color: '#9370db', icon: 'interior' },
  { id: 'roof', name: 'Roof', color: '#696969', icon: 'building' },
  { id: 'foundation', name: 'Foundation', color: '#a0522d', icon: 'structural' },
  // Documentation
  { id: 'existing', name: 'Existing Conditions', color: '#708090', icon: 'camera' },
  { id: 'verification', name: 'Verification', color: '#32cd32', icon: 'check' },
  { id: 'punch', name: 'Punch List', color: '#ff6347', icon: 'warning' },
  { id: 'closeout', name: 'Closeout', color: '#4169e1', icon: 'check' },
];

export const CATEGORY_ICONS = [
  'camera', 'warning', 'check', 'flag', 'pin', 'star', 'info', 'tool',
  'building', 'electrical', 'plumbing', 'hvac', 'structural', 'fire', 'exterior', 'interior', 'drone'
] as const;
export type CategoryIcon = typeof CATEGORY_ICONS[number];

// Photo Type options
export const PHOTO_TYPES = [
  'Documentation',
  'Issue',
  'Progress',
  'Safety',
  'Quality Control',
  'Inspection',
  'As-Built',
  'RFI',
  'Change Order',
  'Punchlist',
  'Closeout',
  'Other',
] as const;
export type PhotoType = typeof PHOTO_TYPES[number];

// Photo Area options
export const PHOTO_AREAS = [
  'Exterior',
  'Interior',
  'Roof',
  'Foundation',
  'Basement',
  'Ground Floor',
  'Upper Floors',
  'Mechanical Room',
  'Electrical Room',
  'Utility',
  'Common Area',
  'Office',
  'Restroom',
  'Kitchen',
  'Storage',
  'Parking',
  'Site',
  'Other',
] as const;
export type PhotoArea = typeof PHOTO_AREAS[number];

// Level/Floor options (generated dynamically but with common presets)
export const DEFAULT_LEVELS = [
  'Basement 2',
  'Basement 1',
  'Ground/1st Floor',
  '2nd Floor',
  '3rd Floor',
  '4th Floor',
  '5th Floor',
  'Roof',
  'Penthouse',
  'Mezzanine',
  'Site/Exterior',
] as const;

export interface PhotoData {
  id: string;
  name: string;
  description?: string;
  notes?: string;
  path: string;
  thumbnailPath?: string;
  // Classification
  categoryId: string;
  photoType?: PhotoType | string;
  photoArea?: PhotoArea | string;
  level?: string;
  // Location
  coordinates: { longitude: number; latitude: number };
  isManuallyPlaced?: boolean;  // True if user placed via drag/click (no EXIF GPS)
  // Timestamps
  timestamp?: string;  // Photo taken time (from EXIF or manual)
  addedAt: string;     // When added to project
}

// Sort options for photo list
export type PhotoSortField = 'name' | 'addedAt' | 'timestamp' | 'category' | 'type' | 'area' | 'level';
export type PhotoSortOrder = 'asc' | 'desc';

export interface PointCloudData {
  id: string;
  name: string;
  path: string;
  /** Cache hash for converted Potree files */
  cacheHash?: string;
  /** File format (las, laz, e57, copc) */
  format?: string;
  /** Number of points in the cloud */
  pointCount?: number;
  /** File size in bytes */
  sizeBytes?: number;
  /** When the file was added */
  addedAt?: string;
  /** Whether the file is stored in S3 */
  isS3?: boolean;
  /** S3 key if stored remotely */
  s3Key?: string;
  /** Direct URL to COPC file (CDN or presigned) — skips Potree conversion */
  copcUrl?: string;
}

export interface PdfData {
  id: string;
  name: string;
  path: string;
}

export interface DownloadAsset {
  id: string;
  name: string;
  url: string;
  description?: string;
  fileType?: string;    // 'pdf', 'las', 'dwg', 'zip', etc.
  fileSize?: number;    // bytes
  enabled: boolean;     // admin toggle — visible to users?
  addedAt: string;      // ISO timestamp
}

export interface ModelData {
  id: string;
  name: string;
  type: 'external' | 'ifc';
  externalUrl?: string;
  ifcPath?: string;
}

export interface MatterportData {
  id: string;
  name: string;
  url: string;
}

export interface ThermalCalibration {
  slope: number;
  offset: number;
  unit: 'celsius' | 'fahrenheit';
}

/** Tile cache metadata for a GeoTIFF file */
export interface TileMetadata {
  originalPath: string;
  originalWidth: number;
  originalHeight: number;
  originalSizeBytes: number;
  tileSize: number;           // 256 or 512
  format: 'webp' | 'png' | 'jpg';
  levels: number;             // Pyramid zoom levels
  bounds?: { minX: number; minY: number; maxX: number; maxY: number };
  boundsWgs84?: { minX: number; minY: number; maxX: number; maxY: number }; // Reprojected to WGS84
  epsg?: number;              // EPSG code of the source CRS
  bandCount: number;
  dataType: 'rgb' | 'rgba' | 'grayscale' | 'dsm';
  minValue?: number;          // For DSM color scaling
  maxValue?: number;
  cacheHash: string;
  createdAt: string;
  tileCount: number;
  // Extended fields for global Web Mercator tiles (e.g., MipMap)
  isGlobalScheme?: boolean;   // True if tiles use global Web Mercator coordinates
  minZoom?: number;           // Minimum zoom level (for global tiles)
  maxZoom?: number;           // Maximum zoom level (for global tiles)
  tileScheme?: 'xyz' | 'tms'; // Tile coordinate scheme (default: xyz)
  isFlatCache?: boolean;      // True if tiles are directly under tile-cache/ (no hash subfolder in S3)
}

import type { FloorPlanData } from './floorplan';

export interface GeoTiffData {
  orthoPath?: string;        // Single file path
  orthoTilesDir?: string;    // Directory containing ortho tiles
  orthoTileCache?: string;   // Hash pointing to tile cache
  orthoTileMetadata?: TileMetadata;

  dsmPath?: string;          // Single file path
  dsmTilesDir?: string;      // Directory containing DSM tiles
  dsmTileCache?: string;     // Hash pointing to tile cache
  dsmTileMetadata?: TileMetadata;

  thermalPath?: string;
  thermalCalibration?: ThermalCalibration;

  // Photo markers
  photos?: PhotoData[];
  customCategories?: PhotoCategory[];  // Per-project custom categories

  // Position offset for manual adjustment (admin only)
  positionOffset?: {
    lng: number;  // Longitude offset in degrees
    lat: number;  // Latitude offset in degrees
  };

  // Floor plan overlays
  floorPlans?: FloorPlanData;
}

// Project category types for card thumbnails
export type ProjectCategory =
  | 'retail'
  | 'industrial'
  | 'commercial'
  | 'residential'
  | 'healthcare'
  | 'education'
  | 'hospitality'
  | 'infrastructure'
  | 'government'
  | 'religious'
  | 'other';

export const PROJECT_CATEGORIES: { id: ProjectCategory; label: string; }[] = [
  { id: 'retail', label: 'Retail' },
  { id: 'industrial', label: 'Industrial' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'residential', label: 'Residential' },
  { id: 'healthcare', label: 'Healthcare' },
  { id: 'education', label: 'Education' },
  { id: 'hospitality', label: 'Hospitality' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'government', label: 'Government' },
  { id: 'religious', label: 'Religious / Worship' },
  { id: 'other', label: 'Other' },
];

export interface ProjectPreferences {
  defaultTab?: TabId;
  pdfPanelOpen?: boolean;
  pdfPanelWidth?: number;
  selectedPdfId?: string;
  selectedPointCloudId?: string;
  selectedMatterportId?: string;
  selectedModelId?: string;
  selectedPhotoId?: string;
  /** true = user manually set the project name (disables auto-generation) */
  nameOverride?: boolean;
  /** true = site number marked as not applicable */
  siteNumberNA?: boolean;
}

export interface Project {
  id: string;
  name: string;
  clientId?: string;
  siteId?: string;
  address?: string;
  createdAt: string;
  updatedAt: string;

  // Project metadata (admin editable)
  client?: string;           // Client company name
  endClient?: string;        // End client name
  city?: string;             // City
  state?: string;            // State/Province
  siteNumber?: string;       // Site/store number
  description?: string;      // Project description/notes
  category?: ProjectCategory; // Industry category for card icon
  thumbnailPath?: string;    // Custom thumbnail image path
  latitude?: number;         // Project site latitude
  longitude?: number;        // Project site longitude

  // Deliverables configuration
  enabledTabs?: TabId[];     // Which tabs are visible (defaults to all if undefined)

  // Legacy single URL support (will be migrated)
  matterportUrl?: string;
  model?: ModelData;
  // New array-based support
  matterportTours: MatterportData[];
  models: ModelData[];
  pointClouds: PointCloudData[];
  geotiff?: GeoTiffData;
  pdfs: PdfData[];
  downloads: DownloadAsset[];
  preferences?: ProjectPreferences;
}

export type TabId = 'matterport' | 'model' | 'pointcloud' | 'geotiff' | 'thermal';

export interface TabConfig {
  id: TabId;
  label: string;
  icon: string;
  shortcut: string;
}

export const TABS: TabConfig[] = [
  { id: 'matterport', label: 'Virtual Tour', icon: 'tour', shortcut: 'Ctrl+1' },
  { id: 'model', label: 'BIM Model', icon: 'cube', shortcut: 'Ctrl+2' },
  { id: 'pointcloud', label: 'Point Cloud', icon: 'scatter', shortcut: 'Ctrl+3' },
  { id: 'geotiff', label: 'Map', icon: 'map', shortcut: 'Ctrl+4' },
  { id: 'thermal', label: 'Thermal', icon: 'temperature', shortcut: 'Ctrl+5' },
];

/** All available tab IDs */
export const ALL_TABS: TabId[] = ['matterport', 'model', 'pointcloud', 'geotiff', 'thermal'];

/**
 * Get enabled tabs for a project.
 * Returns all tabs if enabledTabs is undefined/null (backward compatibility for legacy projects).
 * An explicit empty array means no tabs are enabled yet.
 */
export function getEnabledTabs(project: Project | null): TabId[] {
  if (!project?.enabledTabs) {
    return ALL_TABS;
  }
  return project.enabledTabs;
}

/**
 * Whether a tab has anything to show for this project (a tab can be enabled
 * before its data is uploaded).
 */
export function tabHasContent(project: Project | null, tabId: TabId): boolean {
  if (!project) return false;
  switch (tabId) {
    case 'matterport': return (project.matterportTours?.length ?? 0) > 0;
    case 'model': return (project.models?.length ?? 0) > 0;
    case 'pointcloud': return (project.pointClouds?.length ?? 0) > 0;
    case 'geotiff': return !!project.geotiff;
    case 'thermal': return !!project.geotiff?.thermalPath;
    default: return false;
  }
}

/**
 * The tab a project should open on: the first enabled tab that has content,
 * else the first enabled tab. Returns null when no tabs are enabled.
 */
export function getDefaultTab(project: Project | null): TabId | null {
  const enabled = getEnabledTabs(project);
  if (enabled.length === 0) return null;
  return enabled.find((t) => tabHasContent(project, t)) ?? enabled[0];
}

/**
 * Check if a specific tab is enabled for a project.
 */
export function isTabEnabled(project: Project | null, tabId: TabId): boolean {
  return getEnabledTabs(project).includes(tabId);
}
